import axios from 'axios';
import moment from 'moment';
import AuthService from './auth-header';
import hrEmailNotificationService, {
    HR_EMAIL_TEMPLATE_KEYS,
    ANNOUNCEMENT_NOTIFICATION_RECIPIENT,
} from './hr-email-notification-service';
import {
    broadcastPushToActiveUsers,
    buildAttendanceDeepLink,
} from '@/utils/fcm-push';
import { isWFHLeaveType } from '@/utils/attendance-leave-types';
import store from '@/store';
// /*dev*/const API_URL = 'https://ats.sattvahuman.in/api/';
// /*prod*/ // const API_URL = 'https://ats.sattvahuman.in/api/';

let API_URL = ''
    if (process.env.NODE_ENV === "production") {
        API_URL = 'https://ats.sattvahuman.in/api/'
    } else {
        API_URL = 'http://192.168.0.30:5000/api/'
    }

// In-memory cache for frequently accessed data
const cache = {
    employeeConfigs: new Map(), // Cache employee configs (TTL: 5 minutes)
    holidays: null, // Cache holidays (TTL: 1 hour)
    holidaysTimestamp: null,
    companyWfhDays: null, // Cache Company-Wide WFH Days (TTL: 1 hour) — mirrors `holidays` above
    companyWfhDaysTimestamp: null,
    pendingRequests: new Map(), // Request deduplication
}

// Cache TTL constants
const EMPLOYEE_CONFIG_TTL = 5 * 60 * 1000 // 5 minutes
const HOLIDAYS_TTL = 60 * 60 * 1000 // 1 hour

function normalizeAttendanceApiError(err) {
    if (err.response && err.response.data) {
        const payload = typeof err.response.data === 'object'
            ? { ...err.response.data }
            : { message: err.response.data }
        if (payload.statusCode === undefined && err.response.status) {
            payload.statusCode = err.response.status
        }
        if (payload.httpStatus === undefined && err.response.status) {
            payload.httpStatus = err.response.status
        }
        return payload
    }
    if (err.response) {
        return {
            status: 'FAILED',
            statusCode: err.response.status,
            httpStatus: err.response.status,
            message: 'Request failed',
            data: null,
        }
    }
    return {
        status: 'FAILED',
        message: err.message || 'Network error',
        data: null,
    }
}

/**
 * BACKEND GAP — `GET/POST /api/company-wfh-day` do not exist on the backend
 * yet (see the "Company-Wide WFH Day" section added to
 * `backend-handoff/PUNCH_ATTENDANCE_API_DOCUMENTATION.md` for the exact endpoint contract
 * the backend team should implement). Until that ships, `getCompanyWfhDays()`
 * and `createCompanyWfhDay()` below transparently fall back to this
 * localStorage-backed list so the feature is fully clickable/usable
 * end-to-end today. Remove this fallback (and the try/catch branches that
 * invoke it) once the real endpoints exist — everything else (permission
 * gating, punch-exemption wiring, calendar flag, email notification) is
 * already written against the final `{ status, data }` shape and will keep
 * working unchanged.
 */
const COMPANY_WFH_DAYS_LOCAL_STORAGE_KEY = 'attendance_company_wfh_days__backend_gap_fallback'

function readCompanyWfhDaysFromLocalStorage() {
    try {
        const raw = localStorage.getItem(COMPANY_WFH_DAYS_LOCAL_STORAGE_KEY)
        const parsed = raw ? JSON.parse(raw) : []
        return Array.isArray(parsed) ? parsed : []
    } catch (e) {
        console.warn('[company-wfh-day] Failed to read localStorage fallback list:', e)
        return []
    }
}

function writeCompanyWfhDaysToLocalStorage(days) {
    try {
        localStorage.setItem(COMPANY_WFH_DAYS_LOCAL_STORAGE_KEY, JSON.stringify(days))
    } catch (e) {
        console.warn('[company-wfh-day] Failed to persist localStorage fallback list:', e)
    }
}

/**
 * Fire-and-forget send — NEVER throws/rejects, so `createAnnouncement`
 * callers (`AttendanceDashboard.vue#handleCreateAnnouncement`) can call this
 * without awaiting it and without risking an unhandled rejection blocking
 * the "Publish" UI flow. Mirrors the exact same resilience contract as
 * `task-service.js#sendTaskEmailSafe` — same underlying
 * `hrEmailNotificationService.sendTemplatedEmail()` -> `mailService.mailHR()`
 * pipeline, reused here rather than inventing a new dispatch path.
 */
/**
 * Fire-and-forget company-wide FCM — never throws. Audience is active staff
 * with registered device tokens (client fan-out; email still uses
 * sattva-all@…). See `broadcastPushToActiveUsers` in fcm-push.js.
 */
function sendHrBroadcastPushSafe({ title, body, type, module, meta = {} }) {
    try {
        broadcastPushToActiveUsers({
            title,
            body: body || '',
            link: buildAttendanceDeepLink(),
            data: {
                module: module || 'announcements',
                type: String(type || 'hr_notice'),
                createdAt: new Date().toISOString(),
                ...meta,
            },
        }).catch(err => {
            console.warn('[hr-push] broadcast failed (non-blocking):', err)
        })
    } catch (err) {
        console.warn('[hr-push] broadcast threw (non-blocking):', err)
    }
}

/**
 * Upsert into the publisher's navbar inbox immediately + POST to
 * /api/notifications when available. Cross-user recipients get rows via
 * backend fan-out (preferred) or foreground FCM create bridge.
 * localStorage alone cannot write other users' inboxes from this browser.
 */
function upsertHrLocalInbox({ id, type, title, message, icon, meta = {} }) {
    try {
        if (!id) return
        const dedupeKey = (meta && meta.dedupeKey) || String(id)
        store.dispatch('notifications/create', {
            forceUnread: true,
            notification: {
                id: String(id),
                type: String(type || 'hr_notice'),
                title: title || 'Notification',
                message: message || '',
                createdAt: new Date().toISOString(),
                read: false,
                link: '/dashboard/attendance',
                icon: icon || 'BellIcon',
                variant: 'light-primary',
                source: 'hr_local',
                meta: { ...meta, dedupeKey },
            },
        }).catch(err => {
            console.warn('[hr-inbox] create failed (non-blocking):', err)
        })
    } catch (err) {
        console.warn('[hr-inbox] local upsert failed (non-blocking):', err)
    }
}

async function sendAnnouncementEmailSafe(payload) {
    try {
        const result = await hrEmailNotificationService.sendTemplatedEmail(payload)
        if (result?.status !== 'OK') {
            console.warn('[announcement-email] send did not complete (non-blocking):', {
                to: payload.to,
                template_key: payload.template_key,
                status: result?.status,
                detail: result?.detail,
            })
        }
        return result
    } catch (err) {
        console.warn('[announcement-email] send threw (non-blocking):', err)
        return { status: 'FAILED' }
    }
}

class AttendanceService {
    async addPost(data) {

        try {
            // Format date to YYYY-MM-DD if date field exists
            if (data.date) {
                const formattedDate = moment(data.date).format('YYYY-MM-DD')
                if (formattedDate && formattedDate !== 'Invalid date') {
                    data.date = formattedDate
                }
            }

            // Check if record already exists (unique constraint on emp_id + date)
            // If exists, update instead of insert
            if (data.emp_id && data.date) {
                const existingRecord = await this.findAttendanceByEmpIdAndDate(data.emp_id, data.date)
                if (existingRecord && existingRecord.id) {
                    // Record exists, update it
                    return await this.update(existingRecord.id, data)
                }
            }

            // Record doesn't exist, create new one
            const res = await axios.post(API_URL + 'attendance', data,
        { 
            headers: AuthService.authHeader()
        });
        return res.data;
        }
        catch (err) {
            console.error('Error adding attendance:', err.response || err.message)
            // If it's a unique constraint error, try to update instead
            if (err.response && err.response.data && err.response.data.error) {
                const error = err.response.data.error
                if (error.name === 'SequelizeUniqueConstraintError' && data.emp_id && data.date) {
                    // Try to find and update the existing record
                    try {
                        const existingRecord = await this.findAttendanceByEmpIdAndDate(data.emp_id, data.date)
                        if (existingRecord && existingRecord.id) {
                            return await this.update(existingRecord.id, data)
                        } else {
                            console.warn(`[AttendanceService] Could not find existing record for ${data.emp_id} on ${data.date}`)
                            // Try to update using emp_id/date in URL as fallback
                            try {
                                const directUpdateRes = await axios.put(
                                    API_URL + `attendance/${data.emp_id}/${data.date}`, 
                                    data,
                                    { headers: AuthService.authHeader() }
                                );
                                return directUpdateRes.data;
                            } catch (directErr) {
                                console.error('[AttendanceService] Direct update failed:', directErr.message)
                                // Return meaningful error message
                                return {
                                    status: 'FAILED',
                                    message: 'Record already exists but could not be updated. Please refresh and try again.',
                                    error: error
                                }
                            }
                        }
                    } catch (updateErr) {
                        console.error('Error updating existing attendance:', updateErr)
                    }
                }
            }
            if (err.response && err.response.data) {
                return err.response.data;
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: null
            };
        }

    }


    async findAttendanceByEmpIdAndDate(emp_id, date) {
        try {
            // Format date to ensure consistency
            const formattedDate = moment(date).format('YYYY-MM-DD')
            
            // Use the working endpoint: getAttendanceByDateRange with same date for start and end.
            // This endpoint is confirmed to exist on the backend, so a successful ('OK') response is
            // authoritative: an empty result set means "no attendance record for this emp_id+date yet",
            // not "try another endpoint". The endpoints below are legacy fallbacks that are not
            // implemented server-side and only exist in case the range endpoint itself is unavailable -
            // they should not be hit on the common path, since doing so just produces noisy 404s without
            // changing the outcome.
            try {
                const rangeRes = await this.getAttendanceByDateRange(formattedDate, formattedDate, emp_id)
                if (rangeRes && rangeRes.status === 'OK' && Array.isArray(rangeRes.data)) {
                    // Find the record matching the exact date (may be undefined if none exists yet)
                    const record = rangeRes.data.find(r => 
                        String(r.emp_id) === String(emp_id) && 
                        moment(r.date).format('YYYY-MM-DD') === formattedDate
                    )
                    // The range endpoint already gave a definitive answer - don't fall through to the
                    // legacy fallback endpoints below, which don't exist on the backend and would only
                    // produce 404s.
                    return record || null
                }
            } catch (rangeErr) {
            }

            // Fallback: Try direct query with emp_id and date (may not exist)
            try {
                const directRes = await axios.get(API_URL + `attendance/${emp_id}/${formattedDate}`, {
                    headers: AuthService.authHeader()
                });
                if (directRes.data && directRes.data.status === 'OK' && directRes.data.data) {
                    return directRes.data.data
                }
            } catch (directErr) {
                // Continue to next approach
            }

            // Second fallback: Use the employee-specific endpoint with date filter
            try {
                const empRes = await axios.get(API_URL + `attendance/employee/${emp_id}`, {
                    params: { date: formattedDate },
                    headers: AuthService.authHeader()
                });
                if (empRes.data && empRes.data.status === 'OK' && empRes.data.data) {
                    // If response is an array, find the matching record
                    if (Array.isArray(empRes.data.data)) {
                        const record = empRes.data.data.find(r => 
                            String(r.emp_id) === String(emp_id) && 
                            moment(r.date).format('YYYY-MM-DD') === formattedDate
                        )
                        if (record) return record
                    } else if (moment(empRes.data.data.date).format('YYYY-MM-DD') === formattedDate) {
                        // If response is a single object with matching date
                        return empRes.data.data
                    }
                }
            } catch (empErr) {
                // If employee endpoint doesn't work, continue to next approach
            }

            // Third fallback: Use general attendance endpoint with query params
            try {
                const res = await axios.get(API_URL + 'attendance', {
                    params: {
                        emp_id: emp_id,
                        date: formattedDate
                    },
                    headers: AuthService.authHeader()
                });
                if (res.data && res.data.status === 'OK' && res.data.data) {
                    // If response is an array, find the matching record
                    if (Array.isArray(res.data.data)) {
                        const record = res.data.data.find(r => 
                            String(r.emp_id) === String(emp_id) && 
                            moment(r.date).format('YYYY-MM-DD') === formattedDate
                        )
                        if (record) return record
                    } else {
                        // If response is a single object
                        return res.data.data
                    }
                }
            } catch (generalErr) {
                // If general endpoint doesn't work, continue to final approach
            }

            // Fourth fallback: Get all attendance for the date and filter client-side
            try {
                const dateRes = await axios.get(API_URL + `attendance/date/${formattedDate}`, {
                    headers: AuthService.authHeader()
                });
                if (dateRes.data && dateRes.data.status === 'OK' && dateRes.data.data) {
                    if (Array.isArray(dateRes.data.data)) {
                        const record = dateRes.data.data.find(r => String(r.emp_id) === String(emp_id))
                        if (record) return record
                    }
                }
            } catch (dateErr) {
                // All approaches failed
            }

            return null
        }
        catch (err) {
            // Silently handle 404 errors as "not found"
            if (err.response && err.response.status === 404) {
                return null
            }
            console.error('Error finding attendance by emp_id and date:', err.response || err.message)
            return null
        }
    }

    async getAttendanceOnDate(date) {
        try {
            const res = await axios.get(API_URL + 'attendance/' + date,
            { 
                headers: AuthService.authHeader()
            });
            return res.data
        }
        catch (err) {
            console.error('Error fetching attendance on date:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data;
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: []
            }
        }
    }

    async update(id, data) {
        try {
            // Format date to YYYY-MM-DD if date field exists
            if (data.date) {
                const formattedDate = moment(data.date).format('YYYY-MM-DD')
                if (formattedDate && formattedDate !== 'Invalid date') {
                    data.date = formattedDate
                }
            }

            const res = await axios.put(API_URL + 'attendance/' + id, data,
            { 
                headers: AuthService.authHeader()
            });
            return res.data
        }
        catch (err) {
            console.error('Error updating attendance:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data;
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: null
            };
        }
    }

    async delete(id) {
        try {
            const res = await axios.delete(API_URL + 'attendance/:' + id,
            { 
                headers: AuthService.authHeader()
            });
            return res.data
        }
        catch (err) {
            return err.response
        }
    }

    async getHolidayList(abortSignal = null) {
        try {
            // Check cache first
            const now = Date.now()
            if (cache.holidays && cache.holidaysTimestamp && (now - cache.holidaysTimestamp) < HOLIDAYS_TTL) {
                return cache.holidays
            }

            // Check if request is already pending (deduplication)
            const cacheKey = 'holiday-list'
            if (cache.pendingRequests.has(cacheKey)) {
                return await cache.pendingRequests.get(cacheKey)
            }

            // Create new request
            const requestPromise = axios.get(API_URL + 'holiday-list/',
                {
                    headers: AuthService.authHeader(),
                    signal: abortSignal
                }).then(res => {
                    // Cache the result
                    cache.holidays = res.data
                    cache.holidaysTimestamp = Date.now()
                    cache.pendingRequests.delete(cacheKey)
                    return res.data
                }).catch(err => {
                    cache.pendingRequests.delete(cacheKey)
                    throw err
                })

            // Store pending request for deduplication
            cache.pendingRequests.set(cacheKey, requestPromise)

            // Return res.data which contains { status, message, data: [...] }
            // The API returns: { status: "OK", message: "...", data: [...] }
            return await requestPromise
        }
        catch (err) {
            if (err.name === 'AbortError' || err.name === 'CanceledError') {
                return {
                    status: 'CANCELLED',
                    message: 'Request cancelled',
                    data: []
                }
            }
            console.error('Error fetching holiday list:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data;
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: []
            }
        }
    }

    async createHoliday(holidayData) {
        try {
            const res = await axios.post(API_URL + 'holiday-list/',
                holidayData,
                {
                    headers: AuthService.authHeader()
                }
            );
            return res.data
        }
        catch (err) {
            console.error('Error creating holiday:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data;
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error'
            }
        }
    }

    /**
     * Get all declared Company-Wide WFH Days.
     * GET /api/company-wfh-day/ — mirrors `getHolidayList()` above
     * (same in-memory TTL cache), with a localStorage fallback for the
     * backend gap — see the comment above `COMPANY_WFH_DAYS_LOCAL_STORAGE_KEY`.
     */
    async getCompanyWfhDays(abortSignal = null) {
        try {
            const now = Date.now()
            if (cache.companyWfhDays && cache.companyWfhDaysTimestamp && (now - cache.companyWfhDaysTimestamp) < HOLIDAYS_TTL) {
                return cache.companyWfhDays
            }

            const res = await axios.get(API_URL + 'company-wfh-day/',
                {
                    headers: AuthService.authHeader(),
                    signal: abortSignal
                }
            )

            const result = res.data
            cache.companyWfhDays = result
            cache.companyWfhDaysTimestamp = Date.now()
            return result
        }
        catch (err) {
            if (err.name === 'AbortError' || err.name === 'CanceledError') {
                return {
                    status: 'CANCELLED',
                    message: 'Request cancelled',
                    data: []
                }
            }

            // BACKEND GAP FALLBACK: /api/company-wfh-day/ is not implemented
            // on the backend yet, so any request failure (404, network error,
            // etc.) falls back to the localStorage-backed list rather than
            // surfacing an error — see comment above
            // `COMPANY_WFH_DAYS_LOCAL_STORAGE_KEY`. Remove this catch-and-
            // fallback branch (keep the try block above as-is) once the real
            // endpoint ships.
            console.warn('[company-wfh-day] GET failed — falling back to localStorage (backend endpoint not implemented yet):', err.response?.status || err.message)
            const localData = readCompanyWfhDaysFromLocalStorage()
            const fallbackResult = { status: 'OK', data: localData, _source: 'localStorage-fallback' }
            cache.companyWfhDays = fallbackResult
            cache.companyWfhDaysTimestamp = Date.now()
            return fallbackResult
        }
    }

    /**
     * Declare a new Company-Wide WFH Day.
     * POST /api/company-wfh-day/ — mirrors `createHoliday()` above.
     *
     * IMPORTANT: this method (and every caller of it) must NEVER call
     * `applyLeave()` / touch any per-employee leave record — a Company-Wide
     * WFH Day is a pure date-level lookup, structurally identical to
     * Holiday, specifically so no individual employee's WFH balance can
     * ever be affected by it. Do not "helpfully" loop this over
     * `applyLeave({ leave_type: 'Work From Home', ... })` for every
     * employee — that is the exact code path that decrements personal WFH
     * balance and must stay completely disconnected from this feature.
     */
    async createCompanyWfhDay(data) {
        try {
            const res = await axios.post(API_URL + 'company-wfh-day/',
                data,
                {
                    headers: AuthService.authHeader()
                }
            );
            // Invalidate the cache so the next getCompanyWfhDays() re-fetches
            // the freshly-created record from the server.
            cache.companyWfhDays = null
            cache.companyWfhDaysTimestamp = null
            return res.data
        }
        catch (err) {
            // BACKEND GAP FALLBACK: see comment above
            // `COMPANY_WFH_DAYS_LOCAL_STORAGE_KEY` / `getCompanyWfhDays()`.
            // Persist directly into the same localStorage list that
            // `getCompanyWfhDays()` reads from, so create -> list works
            // end-to-end today with zero backend support. Remove this
            // catch-and-fallback branch once the real endpoint ships.
            console.warn('[company-wfh-day] POST failed — falling back to localStorage (backend endpoint not implemented yet):', err.response?.status || err.message)
            try {
                const existing = readCompanyWfhDaysFromLocalStorage()
                const newRecord = {
                    id: `local-${Date.now()}`,
                    date: data.date,
                    title: data.title,
                    pune: data.pune || 'NO',
                    delhi: data.delhi || 'NO',
                    created_by: data.created_by || null,
                    created_at: new Date().toISOString(),
                    _source: 'localStorage-fallback',
                }
                existing.push(newRecord)
                writeCompanyWfhDaysToLocalStorage(existing)
                cache.companyWfhDays = null
                cache.companyWfhDaysTimestamp = null
                return { status: 'OK', data: newRecord }
            } catch (localErr) {
                console.error('Error creating company WFH day (localStorage fallback also failed):', localErr)
                return {
                    status: 'FAILED',
                    message: err.message || 'Network error'
                }
            }
        }
    }

    async getAttendanceByDateRange(startDate, endDate, emp_id = null) {
        try {
            // Validate emp_id is provided
            if (!emp_id) {
                console.warn('getAttendanceByDateRange: emp_id is required')
                return {
                    status: 'FAILED',
                    message: 'Employee ID is required',
                    data: []
                }
            }
            
            const res = await axios.get(API_URL + 'attendance/range/' + emp_id, {
                params: {
                    start_date: startDate,
                    end_date: endDate
                },
                headers: AuthService.authHeader()
            })
            return res.data
        }
        catch (err) {
            console.error('Error fetching attendance by date range:', err.response || err.message)
            // Check if it's a 404 error
            if (err.response && err.response.status === 404) {
                return {
                    status: 'FAILED',
                    message: 'Endpoint not found (404)',
                    data: []
                }
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: []
            }
        }
    }

    async getPunchAttendanceByDateRange(startDate, endDate, emp_id = null) {
        try {
            // Build query parameters
            const params = new URLSearchParams()
            if (startDate) {
                params.append('start_date', startDate)
            }
            if (endDate) {
                params.append('end_date', endDate)
            }
            if (emp_id) {
                params.append('emp_id', emp_id)
            }

            const url = API_URL + 'punch-attendance' + (params.toString() ? '?' + params.toString() : '')
            const res = await axios.get(url, {
                headers: AuthService.authHeader()
            })

            if (res.data && res.data.status === 'OK' && res.data.data) {
                const firstTruthy = (...vals) => {
                    for (const v of vals) {
                        if (v == null || v === '') continue
                        const s = String(v).trim()
                        if (!s || s === '0:00:00' || s === '00:00:00' || s === '0:00' || s === '00:00') continue
                        return v
                    }
                    return null
                }

                // Parse ISO / SQL / time-only / malformed "T," timestamps → moment (local)
                const parsePunchMoment = (raw) => {
                    if (raw == null || raw === '') return null
                    const cleaned = String(raw).trim().replace('T,', 'T')
                    if (!cleaned) return null
                    const formats = [
                        moment.ISO_8601,
                        'YYYY-MM-DDTHH:mm:ss.SSSZ',
                        'YYYY-MM-DDTHH:mm:ssZ',
                        'YYYY-MM-DDTHH:mm:ss',
                        'YYYY-MM-DD HH:mm:ss',
                        'YYYY-MM-DD HH:mm',
                        'HH:mm:ss',
                        'HH:mm',
                        'H:mm:ss',
                        'H:mm',
                    ]
                    const m = moment(cleaned, formats, true)
                    if (m.isValid()) return m
                    const loose = moment(cleaned)
                    return loose.isValid() ? loose : null
                }

                const formatHms = (m) => (m && m.isValid() ? m.format('HH:mm:ss') : null)
                const formatYmdLocal = (m) => (m && m.isValid() ? m.format('YYYY-MM-DD') : null)

                // Transform punch-attendance data to match expected format
                const transformedData = res.data.data.map(record => {
                    const punchDetails = record.punch_details || {}
                    const punchInDetails = punchDetails.punch_in_details || punchDetails.punchInDetails || {}
                    const punchOutDetails = punchDetails.punch_out_details || punchDetails.punchOutDetails || {}

                    // Prefer nested punch_details, then top-level API aliases
                    const rawIn = firstTruthy(
                        punchInDetails.punch_in_time,
                        punchInDetails.punchInTime,
                        punchInDetails.time,
                        punchInDetails.in_time,
                        punchInDetails.intime,
                        punchDetails.punch_in_time,
                        record.punch_in_time,
                        record.punchInTime,
                        record.punch_in,
                        record.in_time,
                        record.intime,
                        record.att_in_time
                    )
                    const rawOut = firstTruthy(
                        punchOutDetails.punch_out_time,
                        punchOutDetails.punchOutTime,
                        punchOutDetails.time,
                        punchOutDetails.out_time,
                        punchOutDetails.outtime,
                        punchDetails.punch_out_time,
                        record.punch_out_time,
                        record.punchOutTime,
                        record.punch_out,
                        record.out_time,
                        record.outtime,
                        record.att_out_time
                    )

                    const punchInMoment = parsePunchMoment(rawIn)
                    const punchOutMoment = parsePunchMoment(rawOut)
                    const createdMoment = parsePunchMoment(record.createdAt || record.created_at || record.date || record.datestring)

                    // Local calendar date (avoid UTC shift from toISOString)
                    let dateStr = formatYmdLocal(punchInMoment) || formatYmdLocal(createdMoment)

                    const intime = formatHms(punchInMoment)
                    const outtime = formatHms(punchOutMoment)

                    let worktime = null
                    if (punchInMoment && punchOutMoment && punchOutMoment.isAfter(punchInMoment)) {
                        const diffMinutes = punchOutMoment.diff(punchInMoment, 'minutes')
                        const diffHours = Math.floor(diffMinutes / 60)
                        const remMinutes = diffMinutes % 60
                        worktime = `${diffHours}:${String(remMinutes).padStart(2, '0')}:00`
                    }

                    return {
                        emp_id: record.emp_id,
                        date: dateStr,
                        intime: intime,
                        outtime: outtime,
                        punch_in_time: rawIn || intime,
                        punch_out_time: rawOut || outtime,
                        worktime: worktime,
                        attendance: intime && outtime ? 'present' : (intime ? 'half-day' : 'absent'),
                        punch_details: punchDetails,
                        createdAt: record.createdAt,
                        updatedAt: record.updatedAt
                    }
                }).filter(record => {
                    if (!record.date || !startDate || !endDate) return !!record.date
                    // String compare YYYY-MM-DD — avoids timezone edge cases from Date()
                    return record.date >= startDate && record.date <= endDate
                })

                return {
                    status: 'OK',
                    data: transformedData
                }
            }
            return res.data
        }
        catch (err) {
            console.error('Error fetching punch attendance by date range:', err.response || err.message)
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: []
            }
        }
    }

    async getLeaveStats(emp_id) {
        try {
            // Fetch leave balance from employee config API
            let totalLeaveBalance = 0 // Total leaves from leave_balance field
            let availableLeaveBalance = 0 // Available leaves from breakdown
            try {
                const configRes = await this.getEmployeeConfig(emp_id)
                if (configRes && configRes.status === 'OK' && configRes.data) {
                    const employeeConfig = configRes.data

                    // Get total leave balance from leave_balance field (for Total Leaves)
                    totalLeaveBalance = employeeConfig.leaveBalance || employeeConfig.leave_balance || 0

                    // Handle both camelCase and snake_case field names
                    let breakdown = employeeConfig.leaveBalanceBreakdown || employeeConfig.leave_balance_breakdown

                    // If breakdown is a string, try to parse it as JSON
                    if (typeof breakdown === 'string') {
                        try {
                            breakdown = JSON.parse(breakdown)
                        } catch (parseErr) {
                            console.warn('Failed to parse leaveBalanceBreakdown as JSON:', parseErr)
                            breakdown = null
                        }
                    }

                    if (breakdown && typeof breakdown === 'object') {
                        // Check if it's the new format with individual leave types
                        if (breakdown.privilege_leave !== undefined ||
                            breakdown.casual_leave !== undefined ||
                            breakdown.sick_leave !== undefined ||
                            breakdown.compensatory_balance !== undefined ||
                            breakdown.carry_forward !== undefined) {
                            // Sum all leave type balances from breakdown (for Available Leaves)
                            // Note: medical_leave and sick_leave are treated as the same
                            const sickOrMedicalLeave = (breakdown.sick_leave || 0) + (breakdown.medical_leave || 0)

                            // Calculate carryforward: distribute over 12 months, accumulate month by month
                            const carryForwardTotal = breakdown.carry_forward || 0
                            const currentMonth = moment().month() + 1 // Month is 0-indexed, so add 1 (1-12)
                            const carryForwardAmount = (carryForwardTotal / 12) * currentMonth // Accumulate month by month

                            availableLeaveBalance = (breakdown.privilege_leave || 0) +
                                (breakdown.casual_leave || 0) +
                                sickOrMedicalLeave + // Combined sick and medical leave
                                (breakdown.compensatory_balance || 0) +
                                carryForwardAmount + // Carry forward balance (monthly accumulation)
                                (breakdown.paternity_leave || 0) +
                                (breakdown.maternity_leave || 0) +
                                (breakdown.emergency_leave || 0) +
                                (breakdown.exam_leave || 0) +
                                (breakdown.loss_of_pay || 0)

                        } else if (breakdown.leaveBalance !== undefined || breakdown.leave_balance !== undefined) {
                            // Fallback to direct leaveBalance field in breakdown
                            availableLeaveBalance = breakdown.leaveBalance || breakdown.leave_balance || 0
                        }
                    } else {
                        // If no breakdown, use totalLeaveBalance as available
                        availableLeaveBalance = totalLeaveBalance
                    }
                } else {
                    console.warn('Employee config API response not OK:', configRes)
                }
            } catch (configErr) {
                console.error('Error fetching employee config for leave balance:', configErr)
                // Continue with leaveBalance = 0 if config fetch fails
            }

            // Use leave API to calculate leave stats - only count APPROVED leaves
            let leavesTaken = 0
            let WFHCount = 0

            try {
                const leavesRes = await this.getMyLeaves(emp_id)
                if (leavesRes && leavesRes.status === 'OK' && leavesRes.data) {
                    const currentYear = new Date().getFullYear()
                    const yearStart = `${currentYear}-01-01`
                    const yearEnd = `${currentYear}-12-31`

                    // Filter leaves for current year and only count APPROVED leaves
                    leavesRes.data.forEach(leave => {
                        const leaveFromDate = new Date(leave.from_date)
                        const leaveToDate = new Date(leave.to_date)
                        const yearStartDate = new Date(yearStart)
                        const yearEndDate = new Date(yearEnd)

                        // Check if leave overlaps with current year
                        if (leaveToDate >= yearStartDate && leaveFromDate <= yearEndDate) {
                            // Only count approved leaves (not pending or rejected)
                            const status = (leave.status || '').toLowerCase()
                            if (status === 'approved' || status === 'manager approved - pending hr') {
                                if (isWFHLeaveType(leave.leave_type)) {
                                    // Calculate WFH days within current year
                                    const from = leaveFromDate < yearStartDate ? yearStartDate : leaveFromDate
                                    const to = leaveToDate > yearEndDate ? yearEndDate : leaveToDate
                                    const wfhDays = Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1
                                    WFHCount += wfhDays
                                    // WFH doesn't deduct from leave balance
                                } else {
                                    // Check if it's a half-day leave
                                    const isHalfDay = leave.leave_type === '1st Half Day' ||
                                                     leave.leave_type === '2nd Half Day' ||
                                                     leave.half_day === true

                                    // Calculate leave days within current year
                                    const from = leaveFromDate < yearStartDate ? yearStartDate : leaveFromDate
                                    const to = leaveToDate > yearEndDate ? yearEndDate : leaveToDate
                                    
                                    // For half-day leaves, use 0.5; otherwise calculate full days
                                    let leaveDays
                                    if (isHalfDay) {
                                        // For half-day, count as 0.5 per day in range
                                        const daysInRange = Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1
                                        leaveDays = daysInRange * 0.5
                                    } else {
                                        leaveDays = Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1
                                    }
                                    
                                    leavesTaken += leaveDays
                                    // Deduct approved leaves from available balance (calculated from breakdown)
                                    availableLeaveBalance = Math.max(0, availableLeaveBalance - leaveDays)
                                }
                            }
                        }
                    })
                }
            } catch (leavesErr) {
                console.warn('Error fetching leaves for stats calculation:', leavesErr)
                // Continue with leavesTaken = 0 if leave fetch fails
            }

            return {
                status: 'OK',
                data: {
                    totalLeaveBalance: totalLeaveBalance, // Total leaves from leave_balance field
                    leaveBalance: availableLeaveBalance, // Available leaves from breakdown minus approved leaves
                    leavesTaken: leavesTaken, // Only approved leaves (leaves availed)
                    WFHCount: WFHCount // Only approved WFH
                }
            }
        }
        catch (err) {
            console.error('Error fetching leave stats:', err.response || err.message)
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: null
            }
        }
    }

    async getMyLeaves(emp_id, filters = {}) {
        try {
            // Build query parameters for filters
            // Backend expects fromDate and endDate (not start_date/end_date)
            const params = new URLSearchParams()
            if (filters.startDate) params.append('fromDate', filters.startDate)
            if (filters.endDate) params.append('endDate', filters.endDate)
            if (filters.leaveType) params.append('leave_type', filters.leaveType)
            if (filters.status) params.append('status', filters.status)

            const queryString = params.toString()
            const url = API_URL + 'leave/my/' + emp_id + (queryString ? '?' + queryString : '')

            // Use dedicated leave API endpoint to get leaves from leave_applications table
            const res = await axios.get(url,
            { 
                headers: AuthService.authHeader()
            });

            if (res.data && res.data.status === 'OK' && res.data.data) {
                // Transform leave data to include calculated fields
                const leaves = res.data.data.map(leave => {
                    // Calculate total days
                    const from = new Date(leave.from_date)
                    const to = new Date(leave.to_date)
                    const totalDays = Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1

                    return {
                        id: leave.id,
                        emp_id: leave.emp_id,
                        from_date: leave.from_date,
                        to_date: leave.to_date,
                        date: leave.date,
                        leave_type: leave.leave_type,
                        type: leave.type || leave.leave_type,
                        issue_type: leave.issue_type,
                        partial_wfh: leave.partial_wfh === true,
                        status: leave.status || 'pending',
                        regularization_status: leave.status || 'pending', // For compatibility with regularization table
                        total_days: totalDays,
                        comment: leave.comment || leave.remarks || '',
                        remarks: leave.comment || leave.remarks || '',
                        applied_date: leave.applied_date || leave.created_at,
                        created_at: leave.created_at,
                        manager_remarks: leave.manager_remarks || leave.manager_comment || null,
                        manager_comment: leave.manager_remarks || leave.manager_comment || null,
                        hr_remarks: leave.hr_remarks || null,
                        hr_update_dt: leave.hr_update_dt || null,
                        manager_update_dt: leave.manager_update_dt || null,
                        hr_id: leave.hr_id || null
                    }
                })

                return {
                    status: 'OK',
                    data: leaves
                }
            }

            return {
                status: 'OK',
                data: []
            }
        }
        catch (err) {
            // Handle 404 and other errors gracefully
            if (err.response && err.response.status === 404) {
                console.warn(`Leave API endpoint not found for emp_id: ${emp_id}. This may indicate the backend route is not configured.`)
                // Return empty array instead of failing
                return {
                    status: 'OK',
                    data: []
                }
            }
            console.error('Error fetching my leaves:', err.response || err.message)
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: []
            }
        }
    }

    async getRegularizationCases(emp_id, startDate = null, endDate = null) {
        try {
            // Use attendance API to get regularization cases
            const currentYear = new Date().getFullYear()
            const yearStart = startDate || `${currentYear}-01-01`
            const yearEnd = endDate || `${currentYear}-12-31`

            // Fetch holidays to exclude them from regularization
            let holidays = []
            try {
                const holidaysRes = await this.getHolidayList()
                if (holidaysRes && holidaysRes.status === 'OK' && holidaysRes.data) {
                    holidays = holidaysRes.data.map(h => h.date || h.holiday_date).filter(Boolean)
                }
            } catch (holidaysErr) {
                console.warn('Error fetching holidays for regularization check:', holidaysErr)
                // Continue without holidays exclusion if fetch fails
            }

            const res = await this.getAttendanceByDateRange(yearStart, yearEnd, emp_id)
            if (res && res.status === 'OK' && res.data) {
                const attendanceRecords = res.data
                // Local calendar day — exclude today (day may still be in progress)
                const today = moment().format('YYYY-MM-DD')

                // Filter records that need regularization
                const regularizationCases = []

                attendanceRecords.forEach(record => {
                    const recordDate = record.date
                    // Only past days (strictly before today); never include today's date
                    if (recordDate && moment(recordDate).isBefore(today, 'day')) {
                        // Check if date is a weekend (Saturday = 6, Sunday = 0)
                        const recordDateObj = new Date(recordDate)
                        const dayOfWeek = recordDateObj.getDay()
                        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

                        // Check if date is a holiday - normalize dates for comparison
                        const isHoliday = holidays.some(holidayDate => {
                            if (!holidayDate) return false
                            // Normalize both dates to YYYY-MM-DD format for comparison
                            const holidayDateNormalized = new Date(holidayDate).toISOString().split('T')[0]
                            return holidayDateNormalized === recordDate
                        })

                        // Skip weekends and holidays only. Company-Wide WFH still
                        // requires punch-in/out (unlike Holiday) and must not
                        // suppress missing-punch regularization.
                        if (isWeekend || isHoliday) {
                            return
                        }

                        // Check for regularization issues
                        // Include "not marked" attendance as a regularization case
                        const needsRegularization =
                            record.attendance === 'attendance-regularization' ||
                            record.attendance === 'not marked' ||
                            (record.leave_type === '' && record.attendance !== 'present' && record.attendance !== 'leave')

                        if (needsRegularization) {
                            let issueType = 'NO Punch Data found'

                            // Set issue type based on attendance status
                            if (record.attendance === 'not marked') {
                                issueType = 'Attendance Not Marked'
                            } else if (record.remarks && record.remarks.includes('IN Time')) {
                                issueType = 'IN Time is missing'
                            } else if (record.remarks && record.remarks.includes('Out Time')) {
                                issueType = 'Out Time is missing'
                            } else if (record.remarks && record.remarks.includes('Less Work')) {
                                issueType = 'Less Work Hours'
                            }

                            regularizationCases.push({
                                datestring: recordDate,
                                issue_type: issueType,
                                worktime: record.worktime || 'N/A',
                                biometricRecord: {
                                    datestring: recordDate,
                                    intime: record.intime || '0:00:00',
                                    outtime: record.outtime || '0:00:00',
                                    worktime: record.worktime || 'N/A'
                                }
                            })
                        }
                    }
                })

                return {
                    status: 'OK',
                    data: regularizationCases
                }
            }

            return {
                status: 'OK',
                data: []
            }
        }
        catch (err) {
            console.error('Error fetching regularization cases:', err.response || err.message)
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: []
            }
        }
    }

    /**
     * Get regularization cases from the new API endpoint
     * @param {string} emp_id - Employee ID
     * @param {string} role - User role (account-manager, program-manager, project-manager, hr, hr-director, admin, master)
     * @param {string} startDate - Optional start date (YYYY-MM-DD)
     * @param {string} endDate - Optional end date (YYYY-MM-DD)
     * @param {string} view - Optional view context: 'team' (only my team) or 'org' (org-wide for HR). When view=team, backend should return only self+team (e.g. get_my_team_regularizations) for all roles including HR; when view=org, HR gets company.
     * @returns {Promise<Object>} Response with self, team, and company arrays
     */
    async getRegularizationCasesFromAPI(emp_id, role, startDate = null, endDate = null, view = null) {
        try {
            const params = new URLSearchParams()
            if (startDate) {
                params.append('start_date', startDate)
            }
            if (endDate) {
                params.append('end_date', endDate)
            }
            if (view === 'team' || view === 'org') {
                params.append('view', view)
            }

            const queryString = params.toString()
            const url = API_URL + `punch-attendance/regularization/${emp_id}/${role}` + (queryString ? '?' + queryString : '')
            
            const res = await axios.get(url, {
                headers: AuthService.authHeader()
            })
            
            return res.data
        } catch (err) {
            console.error('Error fetching regularization cases from API:', err.response || err.message)
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                self: [],
                team: [],
                company: []
            }
        }
    }

    async getBiometricByDateRange(startDate, endDate, empcode = null) {
        try {
            let url = API_URL + 'attendance-biometric/date-range/' + startDate + '/' + endDate
            if (empcode) {
                url += '?empcode=' + empcode
            }
            const res = await axios.get(url,
                {
                    headers: AuthService.authHeader()
                });
            return res.data
        }
        catch (err) {
            console.error('Error fetching biometric data:', err.response || err.message)
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: []
            }
        }
    }

    async applyLeave(data) {
        try {
            // Format dates to YYYY-MM-DD if date fields exist
            if (data.from_date) {
                let fromMoment
                if (data.from_date instanceof Date) {
                    fromMoment = moment(data.from_date)
                } else if (typeof data.from_date === 'string') {
                    // Try parsing with multiple formats
                    fromMoment = moment(data.from_date, ['YYYY-MM-DD', 'DD-MM-YYYY', 'MM-DD-YYYY', 'YYYY/MM/DD', 'DD/MM/YYYY'], true)
                    if (!fromMoment.isValid()) {
                        // Fallback to default parsing
                        fromMoment = moment(data.from_date)
                    }
                } else {
                    fromMoment = moment(data.from_date)
                }

                const formattedFromDate = fromMoment.isValid() ? fromMoment.format('YYYY-MM-DD') : null

                if (formattedFromDate && formattedFromDate !== 'Invalid date') {
                    data.from_date = formattedFromDate
                } else {
                    throw new Error(`Invalid from_date format: ${data.from_date}`)
                }
            }
            if (data.to_date) {
                let toMoment
                if (data.to_date instanceof Date) {
                    toMoment = moment(data.to_date)
                } else if (typeof data.to_date === 'string') {
                    // Try parsing with multiple formats
                    toMoment = moment(data.to_date, ['YYYY-MM-DD', 'DD-MM-YYYY', 'MM-DD-YYYY', 'YYYY/MM/DD', 'DD/MM/YYYY'], true)
                    if (!toMoment.isValid()) {
                        // Fallback to default parsing
                        toMoment = moment(data.to_date)
                    }
                } else {
                    toMoment = moment(data.to_date)
                }

                const formattedToDate = toMoment.isValid() ? toMoment.format('YYYY-MM-DD') : null

                if (formattedToDate && formattedToDate !== 'Invalid date') {
                    data.to_date = formattedToDate
                } else {
                    throw new Error(`Invalid to_date format: ${data.to_date}`)
                }
            }

            const res = await axios.post(API_URL + 'leave/apply', data,
                {
                    headers: AuthService.authHeader()
                });
            return res.data
        }
        catch (err) {
            console.error('Error applying leave:', err.response || err.message)
            return normalizeAttendanceApiError(err)
        }
    }

    async updateLeave(data) {
        try {
            // Format dates to YYYY-MM-DD if date fields exist
            if (data.from_date) {
                let fromMoment
                if (data.from_date instanceof Date) {
                    fromMoment = moment(data.from_date)
                } else if (typeof data.from_date === 'string') {
                    // Try parsing with multiple formats
                    fromMoment = moment(data.from_date, ['YYYY-MM-DD', 'DD-MM-YYYY', 'MM-DD-YYYY', 'YYYY/MM/DD', 'DD/MM/YYYY'], true)
                    if (!fromMoment.isValid()) {
                        // Fallback to default parsing
                        fromMoment = moment(data.from_date)
                    }
                } else {
                    fromMoment = moment(data.from_date)
                }

                const formattedFromDate = fromMoment.isValid() ? fromMoment.format('YYYY-MM-DD') : null

                if (formattedFromDate && formattedFromDate !== 'Invalid date') {
                    data.from_date = formattedFromDate
                } else {
                    throw new Error(`Invalid from_date format: ${data.from_date}`)
                }
            }
            if (data.to_date) {
                let toMoment
                if (data.to_date instanceof Date) {
                    toMoment = moment(data.to_date)
                } else if (typeof data.to_date === 'string') {
                    // Try parsing with multiple formats
                    toMoment = moment(data.to_date, ['YYYY-MM-DD', 'DD-MM-YYYY', 'MM-DD-YYYY', 'YYYY/MM/DD', 'DD/MM/YYYY'], true)
                    if (!toMoment.isValid()) {
                        // Fallback to default parsing
                        toMoment = moment(data.to_date)
                    }
                } else {
                    toMoment = moment(data.to_date)
                }

                const formattedToDate = toMoment.isValid() ? toMoment.format('YYYY-MM-DD') : null

                if (formattedToDate && formattedToDate !== 'Invalid date') {
                    data.to_date = formattedToDate
                } else {
                    throw new Error(`Invalid to_date format: ${data.to_date}`)
                }
            }

            // Use PUT method for update and include the leave ID in the URL
            const res = await axios.put(API_URL + 'leave/update/' + data.id, data,
                {
                    headers: AuthService.authHeader()
                });
            return res.data
        }
        catch (err) {
            console.error('Error updating leave:', err.response || err.message)
            return normalizeAttendanceApiError(err)
        }
    }

    async getEmployeeConfig(emp_id, abortSignal = null) {
        try {
            // Check cache first
            const cacheKey = `employee-config-${emp_id}`
            const cached = cache.employeeConfigs.get(cacheKey)
            const now = Date.now()

            if (cached && (now - cached.timestamp) < EMPLOYEE_CONFIG_TTL) {
                return cached.data
            }

            // Check if request is already pending (deduplication)
            if (cache.pendingRequests.has(cacheKey)) {
                return await cache.pendingRequests.get(cacheKey)
            }

            // Create new request
            const requestPromise = axios.get(API_URL + 'employee/config/' + emp_id,
                {
                    headers: AuthService.authHeader(),
                    signal: abortSignal
                }).then(res => {
                    // Cache the result
                    cache.employeeConfigs.set(cacheKey, {
                        data: res.data,
                        timestamp: Date.now()
                    })
                    cache.pendingRequests.delete(cacheKey)
                    return res.data
                }).catch(err => {
                    cache.pendingRequests.delete(cacheKey)
                    throw err
                })

            // Store pending request for deduplication
            cache.pendingRequests.set(cacheKey, requestPromise)

            return await requestPromise
        }
        catch (err) {
            if (err.name === 'AbortError' || err.name === 'CanceledError') {
                return {
                    status: 'CANCELLED',
                    message: 'Request cancelled',
                    data: null
                }
            }
            console.error('Error fetching employee config:', err.response || err.message)
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: null
            }
        }
    }

    // Clear cache methods for testing or manual refresh
    clearEmployeeConfigCache(emp_id = null) {
        if (emp_id) {
            cache.employeeConfigs.delete(`employee-config-${emp_id}`)
        } else {
            cache.employeeConfigs.clear()
        }
    }

    clearHolidaysCache() {
        cache.holidays = null
        cache.holidaysTimestamp = null
    }

    async getLeaveTypeOptions(emp_id, date = null) {
        try {
            let url = API_URL + 'leave/leave-type-options/' + emp_id
            if (date) {
                url += '?date=' + date
            }
            const res = await axios.get(url,
                {
                    headers: AuthService.authHeader()
                });
            return res.data
        }
        catch (err) {
            console.error('Error fetching leave type options:', err.response || err.message)
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: []
            }
        }
    }

    /**
     * Fetch all leave types from the database
     * Returns the global leave types list (not employee-specific)
     * @returns {Promise<{status: string, data: Array}>}
     */
    async getLeaveTypes() {
        try {
            const res = await axios.get(API_URL + 'leave-types', {
                headers: AuthService.authHeader()
            })
            return res.data
        }
        catch (err) {
            console.error('Error fetching leave types:', err.response || err.message)
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: []
            }
        }
    }

    async approveLeave(data) {
        try {
            // Support both old format (backward compatibility) and new format
            let requestData
            if (typeof data === 'object' && data.leave_id) {
                // New format: object with leave_id
                requestData = data
            } else if (Array.isArray(data) || (typeof data === 'object' && data[0])) {
                // Old format: array of parameters
                requestData = {
                    leave_id: data[0],
                    approver_id: data[1],
                    approver_type: data[2] || 'hr',
                    comment: data[3] || '',
                    status: data[4] || 'Approved',
                    is_compensatory: data[5] || false,
                    approver_comment: data[6] || '',
                    approved_by: data[7],
                    approvedAt: data[8]
                }
            } else {
                requestData = data
            }

            const res = await axios.post(API_URL + 'leave/approve', requestData,
                {
                    headers: AuthService.authHeader()
                });
            return res.data
        }
        catch (err) {
            console.error('Error approving leave:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data;
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: null
            }
        }
    }

    /**
     * Reject or cancel a leave request.
     * POST /api/leave/reject
     * Used for both rejection (status: 'HR Reject' | 'Rejected By Manager') and cancellation (status: 'Cancelled').
     * @param {Object|Array} data - Payload: { leave_id, approver_id, approver_type, comment, status, rejected_by, rejectedAt, approver_comment }
     *   - status: 'HR Reject' | 'Rejected By Manager' | 'Cancelled'
     * @returns {Promise<Object>} API response
     */
    async rejectLeave(data) {
        try {
            // Support both old format (backward compatibility) and new format
            let requestData
            if (typeof data === 'object' && data.leave_id) {
                // New format: object with leave_id, status, etc.
                requestData = { ...data }
            } else if (Array.isArray(data) || (typeof data === 'object' && data[0])) {
                // Old format: array of parameters
                requestData = {
                    leave_id: data[0],
                    approver_id: data[1],
                    approver_type: data[2] || 'hr',
                    comment: data[3] || '',
                    approver_comment: data[4] || '',
                    status: data[5],
                    rejected_by: data[6],
                    rejectedAt: data[7]
                }
            } else {
                requestData = data
            }

            const res = await axios.post(API_URL + 'leave/reject', requestData,
                {
                    headers: AuthService.authHeader()
                });
            return res.data
        }
        catch (err) {
            console.error('Error rejecting leave:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data;
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: null
            }
        }
    }

    /**
     * Cancel an approved leave (user cancels their own leave).
     * POST /api/leave/cancel
     * @param {number} leave_id - Leave application ID
     * @param {string} emp_id - Employee ID (leave owner)
     * @returns {Promise<Object>} API response
     */
    async cancelLeave(leave_id, emp_id) {
        try {
            const res = await axios.post(API_URL + 'leave/cancel', { leave_id, emp_id, status: 'Cancelled' }, {
                headers: AuthService.authHeader()
            })
            return res.data
        } catch (err) {
            console.error('Error cancelling leave:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: null
            }
        }
    }

    async getPendingLeavesForHR(emp_id = null) {
        try {
            let url = API_URL + 'leave/pending/hr'
            if (emp_id) {
                url += '?emp_id=' + emp_id
            }
            const res = await axios.get(url,
                {
                    headers: AuthService.authHeader()
                });
            return res.data
        }
        catch (err) {
            console.error('Error fetching pending leaves for HR:', err.response || err.message)
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: []
            }
        }
    }

    /**
     * Create a new announcement
     * POST /api/announcement
     */
    async createAnnouncement(data) {
        try {
            const res = await axios.post(API_URL + 'announcement', data,
                {
                    headers: AuthService.authHeader()
                });
            return res.data
        }
        catch (err) {
            console.error('Error creating announcement:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data;
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: null
            }
        }
    }

    /**
     * "New announcement" notification:
     *   1) Email → fixed `sattva-all@sattvahuman.com` list (unchanged)
     *   2) FCM push → active staff with registered tokens (client fan-out;
     *      email distribution lists cannot be targeted by FCM)
     *   3) Local navbar inbox upsert for the publisher; online recipients
     *      get inbox via foreground FCM handler
     *
     * Fire-and-forget, never throws — see `sendAnnouncementEmailSafe` /
     * `sendHrBroadcastPushSafe` — so it can never block or delay the
     * "Publish" UI flow in `AttendanceDashboard.vue#handleCreateAnnouncement`.
     */
    notifyAnnouncementPosted({ title, message, date, postedByName, announcementId }) {
        sendAnnouncementEmailSafe({
            template_key: HR_EMAIL_TEMPLATE_KEYS.ANNOUNCEMENT_POSTED,
            to: ANNOUNCEMENT_NOTIFICATION_RECIPIENT,
            variables: {
                AnnouncementTitle: title || '-',
                AnnouncementMessage: message || '-',
                AnnouncementDate: date || '-',
                PostedBy: postedByName || 'A team member',
            },
        })

        const pushTitle = 'New announcement'
        const pushBody = title
            ? `${title}${postedByName ? ` — ${postedByName}` : ''}`
            : (message || 'A new company announcement was posted.')
        const metaId = announcementId != null ? String(announcementId) : `${date || ''}:${title || ''}`
        sendHrBroadcastPushSafe({
            title: pushTitle,
            body: pushBody,
            type: 'announcement_posted',
            module: 'announcements',
            meta: { announcementId: metaId },
        })
        upsertHrLocalInbox({
            id: `announcement:${metaId}`,
            type: 'announcement_posted',
            title: pushTitle,
            message: pushBody,
            icon: 'BellIcon',
            meta: { date, postedByName, announcementId: metaId, dedupeKey: `announcement:${metaId}` },
        })
    }

    /**
     * "New holiday" FCM + local inbox. No email today (holidays were silent
     * before push). Same company-wide active-staff fan-out as announcements.
     * Fire-and-forget, never throws.
     */
    notifyHolidayAdded({ holiday, date, location, year, holidayId, postedByName }) {
        const locationLabel = location || 'All Locations'
        const pushTitle = 'New holiday added'
        const pushBody = holiday
            ? `${holiday} on ${date || '—'}${locationLabel ? ` (${locationLabel})` : ''}`
            : `A new holiday was added${date ? ` for ${date}` : ''}.`
        const metaId = holidayId != null ? String(holidayId) : `${date || ''}:${holiday || ''}`
        sendHrBroadcastPushSafe({
            title: pushTitle,
            body: pushBody,
            type: 'holiday_added',
            module: 'holidays',
            meta: { holidayId: metaId },
        })
        upsertHrLocalInbox({
            id: `holiday:${metaId}`,
            type: 'holiday_added',
            title: pushTitle,
            message: pushBody,
            icon: 'CalendarIcon',
            meta: {
                date,
                year,
                location: locationLabel,
                postedByName,
                holidayId: metaId,
                dedupeKey: `holiday:${metaId}`,
            },
        })
    }

    /**
     * "Company-Wide WFH Day declared" — email to sattva-all@… (unchanged)
     * plus the same FCM / local-inbox path as announcements. Fire-and-forget,
     * never throws.
     */
    notifyCompanyWfhDayAnnounced({ date, reason, location, postedByName, companyWfhId }) {
        sendAnnouncementEmailSafe({
            template_key: HR_EMAIL_TEMPLATE_KEYS.COMPANY_WFH_DAY_ANNOUNCED,
            to: ANNOUNCEMENT_NOTIFICATION_RECIPIENT,
            variables: {
                CompanyWfhDate: date || '-',
                CompanyWfhReason: reason || '-',
                CompanyWfhLocation: location || 'All Locations',
                PostedBy: postedByName || 'A team member',
            },
        })

        const pushTitle = 'Company-wide WFH day'
        const pushBody = reason
            ? `${reason} on ${date || '—'} (${location || 'All Locations'})`
            : `A company-wide WFH day was announced${date ? ` for ${date}` : ''}.`
        const metaId = companyWfhId != null ? String(companyWfhId) : `${date || ''}:${reason || ''}`
        sendHrBroadcastPushSafe({
            title: pushTitle,
            body: pushBody,
            type: 'company_wfh_announced',
            module: 'company_wfh',
            meta: { companyWfhId: metaId },
        })
        upsertHrLocalInbox({
            id: `company_wfh:${metaId}`,
            type: 'company_wfh',
            title: pushTitle,
            message: pushBody,
            icon: 'HomeIcon',
            meta: {
                date,
                location,
                postedByName,
                companyWfhId: metaId,
                dedupeKey: `company_wfh:${metaId}`,
            },
        })
    }

    /**
     * Get all announcements
     * GET /api/announcement
     */
    async getAnnouncements() {
        try {
            const res = await axios.get(API_URL + 'announcement',
                {
                    headers: AuthService.authHeader()
                });
            return res.data
        }
        catch (err) {
            console.error('Error fetching announcements:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data;
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: []
            }
        }
    }

    /**
     * Update announcement status
     * Try PUT method first (consistent with job status updates pattern)
     * If that fails with 404, try PUT to base endpoint
     */
    async updateAnnouncementStatus(announcementId, status) {
        try {
            // Try PUT /api/announcement/:id/status first (like job status updates)
            try {
                const res = await axios.put(API_URL + 'announcement/' + announcementId + '/status',
                    { status },
                    { headers: AuthService.authHeader() }
                );
                return res.data
            } catch (putErr) {
                // If PUT to /status endpoint fails with 404, try PUT to base endpoint
                if (putErr.response && putErr.response.status === 404) {
                    const res = await axios.put(API_URL + 'announcement/' + announcementId,
                        { status },
                        { headers: AuthService.authHeader() }
                    );
                    return res.data
                }
                // Re-throw if it's not a 404 error
                throw putErr
            }
        } catch (err) {
            console.error('Error updating announcement status:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data;
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error'
            }
        }
    }

    async getPendingRegularizations(managerId = null) {
        try {
            let url = API_URL + 'attendance/regularization/pending'
            if (managerId) {
                url += '?manager_id=' + managerId
            }
            const res = await axios.get(url,
                {
                    headers: AuthService.authHeader()
                });
            return res.data
        }
        catch (err) {
            console.error('Error fetching pending regularizations:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data;
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: []
            }
        }
    }

    async approveRegularization(data) {
        try {
            // Support both old format (backward compatibility) and new format
            let payload
            if (typeof data === 'object' && data.attendance_id) {
                // New format: object with attendance_id
                payload = data
            } else if (Array.isArray(data) || (typeof data === 'object' && data[0])) {
                // Old format: array of parameters
                payload = {
                    attendance_id: data[0],
                    approver_id: data[1],
                    approver_type: data[2] || 'manager',
                    comment: data[3] || '',
                    status: data[4],
                    approved_by: data[5],
                    approvedAt: data[6],
                    approver_comment: data[7] || '',
                    hrId: data[8],
                    managerUpdateDt: data[9],
                    hrUpdateDt: data[10]
                }
            } else {
                payload = data
            }

            // Add manager_update_dt if manager is approving
            if (payload.approver_type === 'manager' && payload.managerUpdateDt) {
                payload.manager_update_dt = payload.managerUpdateDt
            }

            // Add hr_update_dt and hr_id if HR is approving
            if ((payload.approver_type === 'hr' || payload.approver_type === 'HR') && payload.hrUpdateDt) {
                payload.hr_update_dt = payload.hrUpdateDt
                if (payload.hrId) {
                    payload.hr_id = payload.hrId
                }
            }

            const res = await axios.post(API_URL + 'attendance/regularization/approve', payload,
                {
                    headers: AuthService.authHeader()
                });
            return res.data
        }
        catch (err) {
            console.error('Error approving regularization:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data;
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: null
            }
        }
    }

    async rejectRegularization(data) {
        try {
            // Support both old format (backward compatibility) and new format
            let payload
            if (typeof data === 'object' && data.attendance_id) {
                // New format: object with attendance_id
                payload = data
            } else if (Array.isArray(data) || (typeof data === 'object' && data[0])) {
                // Old format: array of parameters
                payload = {
                    attendance_id: data[0],
                    approver_id: data[1],
                    approver_type: data[2] || 'manager',
                    reason: data[3] || '',
                    status: data[4],
                    rejected_by: data[5],
                    rejectedAt: data[6],
                    approver_comment: data[7] || '',
                    hrId: data[8],
                    managerUpdateDt: data[9],
                    hrUpdateDt: data[10]
                }
            } else {
                payload = data
            }

            // Add manager_update_dt if manager is rejecting
            if (payload.approver_type === 'manager' && payload.managerUpdateDt) {
                payload.manager_update_dt = payload.managerUpdateDt
            }

            // Add hr_update_dt and hr_id if HR is rejecting
            if ((payload.approver_type === 'hr' || payload.approver_type === 'HR') && payload.hrUpdateDt) {
                payload.hr_update_dt = payload.hrUpdateDt
                if (payload.hrId) {
                    payload.hr_id = payload.hrId
                }
            }

            const res = await axios.post(API_URL + 'attendance/regularization/reject', payload,
                {
                    headers: AuthService.authHeader()
                });
            return res.data
        }
        catch (err) {
            console.error('Error rejecting regularization:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data;
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: null
            }
        }
    }

    async getPunchConfig(emp_id) {
        try {
            const res = await axios.get(API_URL + 'punch-config/' + emp_id,
                {
                    headers: AuthService.authHeader()
                });
            return res.data
        }
        catch (err) {
            console.error('Error fetching punch config:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data;
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: null
            }
        }
    }

    // ============================================
    // NEW CONSOLIDATED DASHBOARD APIs
    // ============================================

    /**
     * Get consolidated dashboard dat   a for an employee (My View)
     * Returns: kpi_stats, calendar_data, leave_balance_breakdown, config, leaves, regularization_cases
     * @param {string} emp_id - Employee ID
     * @param {object} params - Query parameters (role, view_mode, month, year)
     */
    async getDashboardData(emp_id, params = {}) {
        try {
            const queryParams = new URLSearchParams()
            if (params.role) queryParams.append('role', params.role)
            if (params.view_mode) queryParams.append('view_mode', params.view_mode)
            if (params.month !== undefined) queryParams.append('month', params.month)
            if (params.year) queryParams.append('year', params.year)
            if (params.selected_employee_id) queryParams.append('selected_employee_id', params.selected_employee_id)

            const queryString = queryParams.toString()
            const url = API_URL + 'attendance/dashboard/' + emp_id + (queryString ? '?' + queryString : '')

            const res = await axios.get(url, {
                headers: AuthService.authHeader()
            })
            return res.data
        }
        catch (err) {
            console.error('Error fetching dashboard data:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: null
            }
        }
    }

    /**
     * Get team dashboard data for managers/HR (Team View)
     * Returns: team_summary, alerts, pending_leave_approvals, pending_regularization_approvals, team_members
     * @param {string} manager_id - Manager/HR Employee ID
     * @param {string} role - User role (account-manager, hr, hr-director, admin, master)
     * @param {object} filters - Filters (selected_manager_id, selected_employee_id, date_range)
     */
    async getTeamDashboard(manager_id, role, filters = {}) {
        try {
            const queryParams = new URLSearchParams()
            queryParams.append('role', role)
            if (filters.selected_manager_id) queryParams.append('selected_manager_id', filters.selected_manager_id)
            if (filters.selected_employee_id) queryParams.append('selected_employee_id', filters.selected_employee_id)
            if (filters.start_date) queryParams.append('start_date', filters.start_date)
            if (filters.end_date) queryParams.append('end_date', filters.end_date)

            const queryString = queryParams.toString()
            const url = API_URL + 'attendance/team-dashboard/' + manager_id + (queryString ? '?' + queryString : '')

            const res = await axios.get(url, {
                headers: AuthService.authHeader()
            })
            return res.data
        }
        catch (err) {
            console.error('Error fetching team dashboard:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: null
            }
        }
    }

    /**
     * Get pre-built calendar data for an employee
     * Returns: days[] with full status for each day (status, leave_type, in_time, out_time, etc.)
     * @param {string} emp_id - Employee ID
     * @param {number} month - Month (0-11)
     * @param {number} year - Year
     */
    async getCalendarData(emp_id, month, year) {
        try {
            const url = API_URL + 'attendance/calendar/' + emp_id + '?month=' + month + '&year=' + year

            const res = await axios.get(url, {
                headers: AuthService.authHeader()
            })
            
            return res.data
        }
        catch (err) {
            console.error('Error fetching calendar data:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: null
            }
        }
    }

    /**
     * Get detailed leave balance with breakdown
     * Returns: total_balance, breakdown[] with assigned, accrued, used, available for each leave type
     * @param {string} emp_id - Employee ID
     */
    async getLeaveBalance(emp_id) {
        try {
            const url = API_URL + 'leave/balance/' + emp_id

            const res = await axios.get(url, {
                headers: AuthService.authHeader()
            })
            return res.data
        }
        catch (err) {
            console.error('Error fetching leave balance:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: null
            }
        }
    }

    /**
     * Validate leave request before submission (pre-flight check)
     * Returns: is_valid, working_days, conflicts, warnings, auto_switch info
     * @param {object} data - Leave validation request { emp_id, from_date, to_date, leave_type, is_half_day }
     */
    async validateLeave(data) {
        try {
            const res = await axios.post(API_URL + 'leave/validate', data, {
                headers: AuthService.authHeader()
            })
            return res.data
        }
        catch (err) {
            console.error('Error validating leave:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: null
            }
        }
    }

    /**
     * Get complete attendance/leave configuration with policies
     * Returns: leave_policy, wfh_policy, probation, reporting, leave_type_options
     * @param {string} emp_id - Employee ID
     */
    async getAttendanceConfig(emp_id) {
        try {
            const url = API_URL + 'attendance/config/' + emp_id

            const res = await axios.get(url, {
                headers: AuthService.authHeader()
            })
            return res.data
        }
        catch (err) {
            console.error('Error fetching attendance config:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: null
            }
        }
    }

    /**
     * Submit regularization request
     * @param {object} data - Regularization data
     */
    async submitRegularization(data) {
        try {
            const res = await axios.post(API_URL + 'attendance/regularization', data, {
                headers: AuthService.authHeader()
            })
            return res.data
        }
        catch (err) {
            console.error('Error submitting regularization:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: null
            }
        }
    }

    /**
     * Get monthly attendance report for a date range
     * @param {string} fromDate - Start date in YYYY-MM-DD format
     * @param {string} toDate - End date in YYYY-MM-DD format
     * @returns {Promise<Object>} Response with attendance report data
     */
    async getMonthlyAttendanceReport(fromDate, toDate) {
        try {
            const res = await axios.get(API_URL + 'attendance/report/monthly', {
                params: {
                    from_date: fromDate,
                    to_date: toDate
                },
                headers: AuthService.authHeader()
            })
            return res.data
        } catch (err) {
            console.error('Error fetching monthly attendance report:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: []
            }
        }
    }

    /**
     * Download monthly attendance report as Excel
     * @param {string} fromDate - Start date in YYYY-MM-DD format
     * @param {string} toDate - End date in YYYY-MM-DD format
     * @returns {Promise<Blob>} Excel file blob
     */
    async downloadMonthlyAttendanceReportExcel(fromDate, toDate) {
        try {
            const res = await axios.get(API_URL + 'attendance/report/monthly', {
                params: {
                    from_date: fromDate,
                    to_date: toDate,
                    format: 'excel' // Request Excel format if backend supports it
                },
                headers: AuthService.authHeader(),
                responseType: 'blob' // Important for file download
            })
            return res.data
        } catch (err) {
            console.error('Error downloading monthly attendance report:', err.response || err.message)
            throw err
        }
    }

    /**
     * Request Compensatory Off Balance (Step 1: Request balance credit)
     * Uses existing leave/apply API with special leave_type 'CO_REQUEST'
     * Balance will be updated via employee/config/ after approval
     * @param {object} data - { emp_id, work_date, comment, requested_by }
     * @returns {Promise<object>} API response
     */
    async requestCompOffBalance(data) {
        // Use existing applyLeave with special type to indicate balance request
        const requestData = {
            emp_id: data.emp_id,
            from_date: data.work_date,
            to_date: data.work_date,
            leave_type: 'CO_REQUEST', // Special type to distinguish from CO leave application
            comment: `CO Balance Request - Work Date: ${data.work_date}. ${data.comment}`,
            applied_by: data.requested_by !== data.emp_id ? data.requested_by : null,
            is_co_balance_request: true, // Flag to indicate this is a balance request
            work_date: data.work_date // Include work_date for backend reference
        }
        
        return this.applyLeave(requestData)
    }

    /**
     * Get my CO balance requests
     * Uses existing getMyLeaves API with filter for CO_REQUEST type
     * @param {string} emp_id - Employee ID
     * @returns {Promise<object>} API response with requests list
     */
    async getMyCompOffBalanceRequests(emp_id) {
        try {
            // Fetch all leaves and filter for CO balance requests (leave_type = 'CO_REQUEST')
            const res = await this.getMyLeaves(emp_id)
            
            if (res && res.status === 'OK' && res.data) {
                // Filter for CO balance requests
                const coRequests = res.data.filter(leave => 
                    leave.leave_type === 'CO_REQUEST' || 
                    leave.leave_type === 'CO Request' ||
                    (leave.comment && leave.comment.includes('CO Balance Request'))
                )
                
                return {
                    status: 'OK',
                    message: 'CO balance requests fetched successfully',
                    data: coRequests
                }
            }
            
            return {
                status: 'OK',
                message: 'No CO balance requests found',
                data: []
            }
        } catch (err) {
            console.error('Error fetching CO balance requests:', err)
            return {
                status: 'FAILED',
                message: err.message || 'Network error',
                data: []
            }
        }
    }

    /**
     * Get organization dashboard data for HR/Admin/Master (Organization View)
     * Returns: org_summary, alerts, pending_leave_approvals, pending_regularization_approvals, all_employees
     * Endpoint: GET /api/attendance/org-dashboard
     * Required permission: read attendance_organization_dashboard OR approve leave_all
     */
    async getOrganizationDashboard(filters = {}) {
        try {
            const queryParams = new URLSearchParams()
            if (filters.selected_manager_id) queryParams.append('selected_manager_id', filters.selected_manager_id)
            if (filters.selected_employee_id) queryParams.append('selected_employee_id', filters.selected_employee_id)
            if (filters.start_date) queryParams.append('start_date', filters.start_date)
            if (filters.end_date) queryParams.append('end_date', filters.end_date)
            if (filters.department) queryParams.append('department', filters.department)
            if (filters.location) queryParams.append('location', filters.location)

            const queryString = queryParams.toString()
            const url = API_URL + 'attendance/org-dashboard' + (queryString ? '?' + queryString : '')

            const res = await axios.get(url, {
                headers: AuthService.authHeader()
            })
            return res.data
        }
        catch (err) {
            console.error('Error fetching organization dashboard:', err.response || err.message)
            if (err.response && err.response.data) {
                return err.response.data
            }
            return {
                status: 'FAILED',
                message: err.message || 'Network error'
            }
        }
    }

}

export default new AttendanceService();
